import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Button,
  Card,
  Form,
  Spinner,
} from "react-bootstrap";
import {
  getLegacyBridgeGlobalSettings,
  isPlausibleBridgeVersionString,
  upsertLegacyBridgeGlobalSettings,
} from "../../api/legacyBridgeSettings";

const PageIntro = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <header className="content__header mb-4">
    <div>
      <h1 className="page-header__title">{title}</h1>
      <p className="page-header__lead">{description}</p>
    </div>
  </header>
);

const LegacyBridgeSettingsPage: React.FC<{ user: User }> = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [minBridgeVersion, setMinBridgeVersion] = useState("");
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<Date | null>(null);
  const [loadedUpdatedBy, setLoadedUpdatedBy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLegacyBridgeGlobalSettings()
      .then((data) => {
        if (cancelled) {
          return;
        }
        setMinBridgeVersion(data.minBridgeVersion ?? "");
        setLoadedUpdatedAt(data.updatedAt ?? null);
        setLoadedUpdatedBy(data.updatedBy ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) {
          return;
        }
        const msg =
          e instanceof Error ? e.message : "Could not load bridge settings.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    if (!isPlausibleBridgeVersionString(minBridgeVersion)) {
      setError(
        "Use a version like 1.0.3 (major.minor.patch), or leave empty for no minimum."
      );
      return;
    }
    setSaving(true);
    try {
      await upsertLegacyBridgeGlobalSettings({
        minBridgeVersion,
        updatedBy: user.email ?? user.uid,
      });
      const next = await getLegacyBridgeGlobalSettings();
      setMinBridgeVersion(next.minBridgeVersion ?? "");
      setLoadedUpdatedAt(next.updatedAt ?? null);
      setLoadedUpdatedBy(next.updatedBy ?? null);
      setSuccess("Saved.");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Could not save bridge settings.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageIntro
        title="Legacy bridge"
        description="Set the minimum MayorX Bridge (Windows desktop) version you expect organizations to run. Apps can read this value to prompt upgrades."
      />

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Card className="shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Loading…</span>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="minBridgeVersion">
                <Form.Label className="fw-semibold">
                  Minimum bridge version
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 1.0.3"
                  value={minBridgeVersion}
                  onChange={(ev) => setMinBridgeVersion(ev.target.value)}
                  disabled={saving}
                  autoComplete="off"
                  style={{ maxWidth: 280 }}
                />
                <Form.Text className="text-muted">
                  Leave empty if you do not want to enforce a minimum. Format:
                  major.minor.patch (optional prerelease suffix).
                </Form.Text>
              </Form.Group>

              {loadedUpdatedAt || loadedUpdatedBy ? (
                <p className="small text-muted mb-3">
                  Last updated
                  {loadedUpdatedAt
                    ? ` ${loadedUpdatedAt.toLocaleString()}`
                    : ""}
                  {loadedUpdatedBy ? ` · ${loadedUpdatedBy}` : ""}
                </p>
              ) : null}

              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default LegacyBridgeSettingsPage;
